// Phase 5B/5C — Display-only expandable panel showing full Parts Assist capture metadata.
// Pulls from existing source_* columns added in Phase 5A. Read-only; no mutations.
// Renders nothing if the part has no source_vendor (manually-entered parts unaffected).
//
// Phase 5C: timestamps now use the shop timezone (formatShopTime) for consistency.
import { useState } from 'react';
import { ChevronDown, ChevronRight, Globe } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { formatShopTime } from '@/utils/formatShopTime';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

interface Props {
  sourceVendor?: string | null;
  sourceLabel?: string | null;
  capturedAt?: string | null;
  availability?: string | null;
  externalId?: string | null;
}

export function CaptureMetadataPanel({
  sourceVendor,
  sourceLabel,
  capturedAt,
  availability,
  externalId,
}: Props) {
  const [open, setOpen] = useState(false);
  const { shopTimezone } = useShopTimezone();
  if (!sourceVendor) return null;

  let captured: string | null = null;
  if (capturedAt) {
    try {
      const d = new Date(capturedAt);
      if (!Number.isNaN(d.getTime())) {
        const zoned = toZonedTime(d, shopTimezone);
        // Date in shop tz + time via shared helper for full consistency.
        captured = `${format(zoned, 'MMM d, yyyy')} · ${formatShopTime(d, shopTimezone, 'short')}`;
      }
    } catch {
      captured = null;
    }
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-1">
      <CollapsibleTrigger className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
        {open ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
        Capture details
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 rounded-sm border border-border/50 bg-muted/30 p-2 text-[10px] text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-1 text-foreground/80">
            <Globe className="h-2.5 w-2.5" />
            <span className="font-medium">{sourceLabel || sourceVendor}</span>
          </div>
          {captured && <div>Captured: {captured}</div>}
          {availability && <div>Availability: {availability}</div>}
          {externalId && <div>Vendor ref: {externalId}</div>}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
